/**
 * CustomRulesList Component
 * Displays and manages custom validation rules
 */

import React, { useState } from "react";
import { useRulesStore } from "../../store/rulesStore";
import type { RuleTemplate, RuleParameter } from "../../types/rules";
import { CustomRuleEditor } from "./CustomRuleEditor";

export function CustomRulesList() {
  const {
    customRuleTemplates,
    customRuleInstances,
    toggleCustomRule,
    updateCustomRuleParameter,
    resetCustomRuleParameters,
    deleteCustomRule,
    addCustomRule,
    updateCustomRule,
  } = useRulesStore();

  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState<RuleTemplate | null>(null);

  const getRuleInstance = (templateId: string) => {
    return customRuleInstances.find(
      (instance) => instance.templateId === templateId
    );
  };

  const toggleExpanded = (templateId: string) => {
    const newExpanded = new Set(expandedRules);
    if (newExpanded.has(templateId)) {
      newExpanded.delete(templateId);
    } else {
      newExpanded.add(templateId);
    }
    setExpandedRules(newExpanded);
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setShowEditor(true);
  };

  const handleEditRule = (template: RuleTemplate) => {
    setEditingRule(template);
    setShowEditor(true);
  };

  const handleSaveRule = (template: RuleTemplate) => {
    if (editingRule) {
      // Update existing rule
      updateCustomRule(editingRule.id, template);
    } else {
      // Add new rule
      addCustomRule(template);
    }
    setShowEditor(false);
    setEditingRule(null);
  };

  const handleDeleteRule = (templateId: string) => {
    if (confirm("Are you sure you want to delete this custom rule?")) {
      deleteCustomRule(templateId);
    }
  };

  const renderParameter = (templateId: string, param: RuleParameter) => {
    const instance = getRuleInstance(templateId);
    const currentValue =
      instance?.parameters.find((p) => p.id === param.id)?.value || param.value;
    const template = customRuleTemplates.find((t) => t.id === templateId);
    const templateParam =
      template?.parameters.find((p) => p.id === param.id) || param;

    return (
      <div
        key={param.id}
        className="bg-dark-300/50 rounded-lg p-4 space-y-3 mb-3"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Label
            </label>
            <input
              type="text"
              value={templateParam.label}
              onChange={(e) => {
                updateCustomRule(templateId, {
                  parameters:
                    template?.parameters.map((p) =>
                      p.id === param.id ? { ...p, label: e.target.value } : p
                    ) || [],
                });
              }}
              className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Type
            </label>
            <select
              value={templateParam.type}
              onChange={(e) => {
                updateCustomRule(templateId, {
                  parameters:
                    template?.parameters.map((p) =>
                      p.id === param.id
                        ? {
                            ...p,
                            type: e.target.value as RuleParameter["type"],
                            value:
                              e.target.value === "boolean"
                                ? false
                                : e.target.value === "number"
                                  ? 0
                                  : "",
                          }
                        : p
                    ) || [],
                });
              }}
              className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
            >
              <option value="number">Number</option>
              <option value="string">String</option>
              <option value="boolean">Boolean</option>
              <option value="select">Select</option>
            </select>
          </div>

          {templateParam.type === "number" && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Value
                </label>
                <input
                  type="number"
                  value={currentValue}
                  onChange={(e) =>
                    updateCustomRuleParameter(
                      templateId,
                      param.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  min={templateParam.min}
                  max={templateParam.max}
                  step={templateParam.step || 0.01}
                  className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Unit
                </label>
                <input
                  type="text"
                  value={templateParam.unit || ""}
                  onChange={(e) => {
                    updateCustomRule(templateId, {
                      parameters:
                        template?.parameters.map((p) =>
                          p.id === param.id ? { ...p, unit: e.target.value } : p
                        ) || [],
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
                  placeholder="e.g., inches, feet"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Min
                </label>
                <input
                  type="number"
                  value={templateParam.min || ""}
                  onChange={(e) => {
                    updateCustomRule(templateId, {
                      parameters:
                        template?.parameters.map((p) =>
                          p.id === param.id
                            ? {
                                ...p,
                                min: parseFloat(e.target.value) || undefined,
                              }
                            : p
                        ) || [],
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">
                  Max
                </label>
                <input
                  type="number"
                  value={templateParam.max || ""}
                  onChange={(e) => {
                    updateCustomRule(templateId, {
                      parameters:
                        template?.parameters.map((p) =>
                          p.id === param.id
                            ? {
                                ...p,
                                max: parseFloat(e.target.value) || undefined,
                              }
                            : p
                        ) || [],
                    });
                  }}
                  className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
                />
              </div>
            </>
          )}

          {templateParam.type === "string" && (
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-300 mb-1">
                Value
              </label>
              <input
                type="text"
                value={currentValue}
                onChange={(e) =>
                  updateCustomRuleParameter(
                    templateId,
                    param.id,
                    e.target.value
                  )
                }
                className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
                placeholder={templateParam.placeholder}
              />
            </div>
          )}

          {templateParam.type === "boolean" && (
            <div className="col-span-2">
              <label className="flex items-center gap-2 text-xs font-medium text-gray-300">
                <input
                  type="checkbox"
                  checked={currentValue}
                  onChange={(e) =>
                    updateCustomRuleParameter(
                      templateId,
                      param.id,
                      e.target.checked
                    )
                  }
                  className="form-checkbox h-4 w-4 text-primary focus:ring-primary border-gray-600 rounded bg-dark-400"
                />
                Value (checked = true)
              </label>
            </div>
          )}

          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-300 mb-1">
              Description
            </label>
            <input
              type="text"
              value={templateParam.description || ""}
              onChange={(e) => {
                updateCustomRule(templateId, {
                  parameters:
                    template?.parameters.map((p) =>
                      p.id === param.id
                        ? { ...p, description: e.target.value }
                        : p
                    ) || [],
                });
              }}
              className="w-full px-2 py-1.5 text-sm bg-dark-400 border border-gray-600 rounded text-white focus:ring-primary focus:border-primary"
              placeholder="Parameter description/help text"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderRuleRow = (template: RuleTemplate) => {
    const instance = getRuleInstance(template.id);
    const isActive = instance?.isActive || false;
    const isExpanded = expandedRules.has(template.id);
    const hasParameters = template.parameters && template.parameters.length > 0;

    return (
      <div
        key={template.id}
        className={`border-b border-gray-700 transition-all ${
          isActive ? "bg-dark-300/20" : ""
        }`}
      >
        <div className="py-3 px-4 hover:bg-dark-300/10">
          <div className="flex items-start gap-4">
            {/* Checkbox */}
            <div className="mt-1">
              <input
                type="checkbox"
                checked={isActive}
                onChange={() => toggleCustomRule(template.id)}
                className="form-checkbox h-4 w-4 text-primary focus:ring-primary border-gray-600 rounded bg-dark-300"
              />
            </div>

            {/* Rule Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className="text-sm font-medium text-white">
                  {template.name}
                </h3>
                {template.standard && (
                  <span className="text-xs text-primary">
                    {template.standard}
                  </span>
                )}
              </div>
              <p className="text-xs text-white mt-1 break-words">
                {template.description}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {hasParameters && (
                  <button
                    onClick={() => toggleExpanded(template.id)}
                    className="text-xs text-white hover:text-gray-300 px-2 py-1 rounded hover:bg-dark-200 whitespace-nowrap"
                    title={isExpanded ? "Hide parameters" : "Show parameters"}
                  >
                    {isExpanded ? "▼" : "▶"} Edit
                  </button>
                )}
                <button
                  onClick={() => handleEditRule(template)}
                  className="text-xs text-white hover:text-gray-300 px-2 py-1 rounded hover:bg-dark-200 whitespace-nowrap"
                  title="Edit rule"
                >
                  Edit Rule
                </button>
                <button
                  onClick={() => resetCustomRuleParameters(template.id)}
                  className="text-xs text-white hover:text-gray-300 px-2 py-1 rounded hover:bg-dark-200 whitespace-nowrap"
                  title="Reset to defaults"
                >
                  Reset
                </button>
                <button
                  onClick={() => handleDeleteRule(template.id)}
                  className="text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded hover:bg-dark-200 whitespace-nowrap"
                  title="Delete rule"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Parameters Section */}
        {isExpanded && hasParameters && (
          <div className="bg-dark-400/20 border-t border-gray-700">
            <div className="py-2">
              {template.parameters.map((param) =>
                renderParameter(template.id, param)
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Group rules by category
  const rulesByCategory = customRuleTemplates.reduce(
    (acc, template) => {
      const category = template.category || "Uncategorized";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(template);
      return acc;
    },
    {} as Record<string, RuleTemplate[]>
  );

  return (
    <div className="space-y-6">
      {/* Add Rule Button */}
      <div className="flex justify-end mb-4">
        <button onClick={handleAddRule} className="btn btn-primary px-4 py-2">
          Add Custom Rule
        </button>
      </div>

      {/* Rules by Category */}
      {Object.entries(rulesByCategory).map(([category, rules]) => {
        const activeRulesInCategory = rules.filter(
          (r) => getRuleInstance(r.id)?.isActive
        ).length;

        return (
          <div
            key={category}
            className="bg-dark-200 rounded-lg overflow-hidden"
          >
            <h2 className="text-sm font-semibold text-white bg-dark-300 px-4 py-2 flex items-center gap-2">
              {category}
              <span className="text-xs text-white ml-auto">
                {activeRulesInCategory}/{rules.length} active
              </span>
            </h2>
            <div className="divide-y divide-gray-700">
              {rules.map((template) => renderRuleRow(template))}
            </div>
          </div>
        );
      })}

      {customRuleTemplates.length === 0 && (
        <div className="text-center py-12 text-white bg-dark-200 rounded-lg">
          <p className="text-lg mb-2">No custom rules yet</p>
          <p className="text-sm text-gray-400 mb-4">
            Create your own validation rules to check custom requirements
          </p>
          <button onClick={handleAddRule} className="btn btn-primary px-4 py-2">
            Create Your First Custom Rule
          </button>
        </div>
      )}

      {/* Rule Editor Modal */}
      {showEditor && (
        <CustomRuleEditor
          rule={editingRule}
          onSave={handleSaveRule}
          onCancel={() => {
            setShowEditor(false);
            setEditingRule(null);
          }}
        />
      )}
    </div>
  );
}

function getCategoryIcon(category: string): string {
  // Return empty string - no icons needed for professional look
  return "";
}
